import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@asm/ui";
import { Radar, AlertCircle } from "lucide-react";
import { adminApi, ReconStats, ReconConfig } from "@/lib/api";
import { ReconOverview } from "@/components/recon/ReconOverview";
import { ReconConfiguration } from "@/components/recon/ReconConfig";
import { ReconDomains } from "@/components/recon/ReconDomains";
import { ReconCampaigns } from "@/components/recon/ReconCampaigns";
import { ReconFindings } from "@/components/recon/ReconFindings";
import { ReconAnalytics } from "@/components/recon/ReconAnalytics";

export default function Recon() {
  const [stats, setStats] = useState<ReconStats | null>(null);
  const [config, setConfig] = useState<ReconConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsData, configData] = await Promise.all([
        adminApi.getReconStats(),
        adminApi.getReconConfig(),
      ]);
      setStats(statsData);
      setConfig(configData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load recon data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleConfigUpdate = async (updates: Partial<ReconConfig>) => {
    try {
      const updated = await adminApi.updateReconConfig(updates);
      setConfig(updated);
      // Refresh stats as well since config changes might affect them
      const newStats = await adminApi.getReconStats();
      setStats(newStats);
    } catch (err) {
      throw err;
    }
  };

  const handleToggleRecon = async (enabled: boolean) => {
    try {
      const updated = enabled
        ? await adminApi.enableRecon()
        : await adminApi.disableRecon();
      setConfig(updated);
      const newStats = await adminApi.getReconStats();
      setStats(newStats);
    } catch (err) {
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="tactical-grid space-y-8">
        <div className="animate-fade-in-up">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <Radar className="h-8 w-8 text-red-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Recon Management</h1>
              <p className="text-muted-foreground">Loading reconnaissance system...</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-red-500/10 flex items-center justify-center animate-pulse">
              <Radar className="h-6 w-6 text-red-500" />
            </div>
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-red-500/20 border-t-red-500" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tactical-grid space-y-8">
        <div className="animate-fade-in-up">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <Radar className="h-8 w-8 text-red-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Recon Management</h1>
              <p className="text-muted-foreground">Manage background passive reconnaissance system</p>
            </div>
          </div>
        </div>
        <div className="animate-fade-in-up rounded-lg border border-red-500/30 bg-red-500/5 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tactical-grid space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <Radar className="h-8 w-8 text-red-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Recon Management</h1>
            <p className="text-muted-foreground">Manage background passive reconnaissance system</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="animate-fade-in-up stagger-1 space-y-6">
        <TabsList className="bg-muted/50 p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-background">
            Overview
          </TabsTrigger>
          <TabsTrigger value="config" className="data-[state=active]:bg-background">
            Configuration
          </TabsTrigger>
          <TabsTrigger value="domains" className="data-[state=active]:bg-background">
            Domains
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="data-[state=active]:bg-background">
            Campaigns
          </TabsTrigger>
          <TabsTrigger value="findings" className="data-[state=active]:bg-background">
            Findings
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-background">
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {stats && config && (
            <ReconOverview
              stats={stats}
              config={config}
              onToggle={handleToggleRecon}
            />
          )}
        </TabsContent>

        <TabsContent value="config">
          {config && (
            <ReconConfiguration
              config={config}
              onUpdate={handleConfigUpdate}
            />
          )}
        </TabsContent>

        <TabsContent value="domains">
          <ReconDomains onImportComplete={fetchData} />
        </TabsContent>

        <TabsContent value="campaigns">
          <ReconCampaigns reconEnabled={stats?.enabled ?? false} />
        </TabsContent>

        <TabsContent value="findings">
          <ReconFindings />
        </TabsContent>

        <TabsContent value="analytics">
          <ReconAnalytics />
        </TabsContent>
      </Tabs>
    </div>
  );
}
